import { useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../api";
import { formatVideoName } from "../pages/admin/videos/hooks/useFetchVideos";
import {
  buildChannelFolderResolver,
  buildZipSuffix,
  collectZipKeys,
  submitZip,
  isTimeoutError,
} from "../utils/downloadHelpers";
import {
  DownloadType,
  getBrandAllowedFileTypes,
} from "../pages/admin/videos/selectedVideoCardV2/helpers";
import { filterRealFiles } from "../pages/videoProduction/helper";
import { useDownloads } from "../components/Downloads";
import { useUser } from "./useUser";
import { isInternal } from "../utils";

interface Options {
  projectId: number;
  projectTitle: string;
  /** When provided, skips the API fetch and uses these files directly. */
  files?: any[];
  /** The project's content_channels, for per-instance zip folders. Pass
   *  alongside `files`; fetched with the project otherwise. */
  channels?: any[];
  /** null = no restriction (e.g. internal users); undefined = resolve from
   *  the fetched project's brands. */
  allowedFileTypes?: Set<string> | null;
  /** Unapproved videos in the requested format, not their watermark (staff,
   *  pre-approval brands). Worked out from the fetched project when omitted. */
  allowUnapproved?: boolean;
}

export function useProjectDownload({
  projectId,
  projectTitle,
  files,
  channels,
  allowedFileTypes,
  allowUnapproved,
}: Options) {
  const [showOptions, setShowOptions] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLink, setDownloadLink] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const { promote } = useDownloads();
  const { role } = useUser();

  const handleDownload = async (type: DownloadType) => {
    setShowOptions(false);
    setIsDownloading(true);
    setDownloadLink("");
    setModalOpen(false);

    const zipFileName = `${formatVideoName(projectTitle || "project")}${buildZipSuffix(type)}.zip`;
    const roomId = Math.random().toString(36).substring(2, 15);

    try {
      let allFiles = files;
      let allChannels = channels;
      let effectiveAllowed = allowedFileTypes;
      let allowUnapprovedFiles = allowUnapproved ?? isInternal(role);
      if (!allFiles) {
        const response = await axiosInstant.get(`/brand-projects/${projectId}`);
        allFiles = filterRealFiles(response.data.projectFiles);
        allChannels = response.data.content_channels;
        if (effectiveAllowed === undefined) {
          effectiveAllowed = getBrandAllowedFileTypes(response.data.brands);
        }
        if (allowUnapproved === undefined) {
          allowUnapprovedFiles =
            isInternal(role) ||
            (response.data.brands || []).some((b: any) => b && b.pre_approval_downloading === true);
        }
      }

      if (!allFiles || !allFiles.length) {
        toast.error("No files available to download.");
        setIsDownloading(false);
        return;
      }

      const loadingId = toast.loading("Zipping files, please wait...");
      const { files: zipEntries, hasContent } = await collectZipKeys(
        allFiles,
        type,
        effectiveAllowed,
        buildChannelFolderResolver(allChannels),
        allowUnapprovedFiles,
      );
      toast.dismiss(loadingId);

      if (!hasContent) {
        toast.error("No content available to download.");
        setIsDownloading(false);
        return;
      }

      const fileName = await submitZip(
        zipEntries,
        zipFileName,
        roomId,
        type === DownloadType.ALL ? { projectId } : undefined,
      );
      if (fileName) {
        setDownloadLink(fileName);
        setModalOpen(true);
      }
      setIsDownloading(false);
    } catch (error: any) {
      if (isTimeoutError(error)) {
        // Long-running zip: hand off to the global download tracker. The
        // top bar takes over (non-blocking) and resolves via the websocket
        // completion event — even if this component unmounts.
        promote(roomId, zipFileName);
      } else {
        toast.error(error.message ?? "Download failed");
      }
      setIsDownloading(false);
    }
  };

  return {
    showOptions,
    setShowOptions,
    isDownloading,
    downloadLink,
    setDownloadLink,
    modalOpen,
    setModalOpen,
    handleDownload,
  };
}
